// The recurrence controls, shared by the series form and the create-event form.
//
// Both forms need the same rule, and the rule is not simple: a weekly repeat
// requires at least one weekday, a custom one an interval, and — the part that is
// easy to get wrong — "days before" plus a TIME is the whole materialization
// schedule. A blank time means the series never generates anything on its own.
// See migration-plan.md §5, "preserve exactly; do not redesign".
//
// This exists as one builder because two copies would drift the moment either
// form was edited, and the fields encode server invariants rather than layout.
// The pieces are handed back individually rather than as one block, because the
// two forms interleave them differently: the series form leads with the rule,
// while the event form tucks it under an off-by-default "Repeats" select.

import {
  MAX_CUSTOM_INTERVAL,
  MAX_DAYS_BEFORE,
  parseWeekdaySet,
  RECURRENCE_TYPES,
  WEEKDAYS,
  WEEKDAY_LABELS,
  type RecurrenceType,
  type Weekday,
} from '@msc/shared';
import { el } from '../dom.js';

const RECURRENCE_LABELS: Record<RecurrenceType, string> = {
  daily: 'Every day',
  weekly: 'Weekly, on chosen days',
  monthly: 'Monthly, on the same date',
  yearly: 'Yearly, on the same date',
  custom: 'Every N days',
};

/** The sentinel for "this is a single event". Never sent to the server. */
export const NO_REPEAT = 'none';

export interface RecurrenceFieldsOptions {
  /** Prefixes every element id, so two of these can coexist on one page. */
  idPrefix: string;
  /**
   * Offer "Does not repeat" as the default. True on the event form, where a
   * repeat is the exception; false on the series form, where the whole point is
   * that it repeats.
   */
  allowNone: boolean;
  /** Present when editing an existing series. */
  existing?: {
    recurrenceType?: RecurrenceType | null;
    recurrenceDays?: string | null;
    recurrenceInterval?: number | null;
    /** The repeat-until date, not a Duration event's end date. */
    endDate?: string | null;
    feedPostDaysBefore?: number | null;
    feedPostTime?: string | null;
  };
  /** Fired whenever the selection changes, including none ⇄ repeating. */
  onChange?: () => void;
}

/** The recurrence subset of a SeriesRequest, or "not a series at all". */
export type RecurrenceChoice =
  | { repeats: false }
  | {
      repeats: true;
      recurrenceType: RecurrenceType;
      recurrenceDays: Weekday[] | null;
      recurrenceInterval: number | null;
      endDate: string | null;
      feedPostDaysBefore: number;
      feedPostTime: string | null;
    };

export interface RecurrenceFields {
  /** Label plus the "Repeats" select. */
  typeRow: HTMLElement;
  /** Only meaningful for `weekly`. */
  weekdayRow: HTMLElement;
  /** Only meaningful for `custom`. */
  intervalRow: HTMLElement;
  /** The repeat-until date. */
  untilRow: HTMLElement;
  /** The "when each occurrence appears" block — days-before plus a time. */
  scheduleBlock: HTMLElement;
  /** False only when `allowNone` and the user left it at "Does not repeat". */
  isRepeating(): boolean;
  value(): RecurrenceChoice;
}

export function recurrenceFields(opts: RecurrenceFieldsOptions): RecurrenceFields {
  const p = opts.idPrefix;
  const existing = opts.existing;

  const typeSelect = el('select', { id: `${p}-recur` }) as HTMLSelectElement;
  if (opts.allowNone) {
    typeSelect.append(el('option', { value: NO_REPEAT }, 'Does not repeat') as HTMLOptionElement);
  }
  // Default to weekly once repeating: it is far and away the common case for the
  // recurring groups this app is for.
  const selected = existing?.recurrenceType ?? (opts.allowNone ? NO_REPEAT : 'weekly');
  for (const type of RECURRENCE_TYPES) {
    const opt = el('option', { value: type }, RECURRENCE_LABELS[type]) as HTMLOptionElement;
    if (type === selected) opt.selected = true;
    typeSelect.append(opt);
  }

  const typeRow = el('div', { class: 'field' }, [
    el('label', { for: `${p}-recur` }, 'Repeats'),
    typeSelect,
  ]);

  const chosenDays = parseWeekdaySet(existing?.recurrenceDays ?? null);
  const dayBoxes = new Map<Weekday, HTMLInputElement>();
  for (const d of WEEKDAYS) {
    const box = el('input', { type: 'checkbox', id: `${p}-day-${d}` }) as HTMLInputElement;
    box.checked = chosenDays.has(d);
    dayBoxes.set(d, box);
  }
  const weekdayRow = el('div', { class: 'field' }, [
    el('label', {}, 'Repeat on'),
    el(
      'div',
      { class: 'weekday-row' },
      WEEKDAYS.map((d) =>
        el('label', { class: 'check', for: `${p}-day-${d}` }, [
          dayBoxes.get(d)!,
          ` ${WEEKDAY_LABELS[d]}`,
        ]),
      ),
    ),
    el('p', { class: 'muted small' }, 'The start date’s own weekday is not included automatically.'),
  ]);

  const intervalInput = numInput(`${p}-interval`, 1, MAX_CUSTOM_INTERVAL, String(existing?.recurrenceInterval ?? 7));
  const intervalRow = el('div', { class: 'field' }, [
    el('label', { for: `${p}-interval` }, 'Days between occurrences'),
    intervalInput,
    el('p', { class: 'muted small' }, `Counted from the start date. 1 to ${MAX_CUSTOM_INTERVAL}.`),
  ]);

  const until = el('input', { id: `${p}-until`, type: 'date' }) as HTMLInputElement;
  until.value = existing?.endDate ?? '';
  const untilRow = el('div', { class: 'field' }, [
    el('label', { for: `${p}-until` }, 'Repeat until'),
    until,
    el('p', { class: 'muted small' }, 'Leave blank to repeat indefinitely.'),
  ]);

  const daysBefore = numInput(
    `${p}-days-before`,
    0,
    MAX_DAYS_BEFORE,
    String(existing?.feedPostDaysBefore ?? 0),
  );
  const postTime = el('input', { id: `${p}-post-time`, type: 'time' }) as HTMLInputElement;
  postTime.value = hhmm(existing?.feedPostTime);

  const scheduleBlock = el('div', { class: 'field' }, [
    el('h3', {}, 'When each occurrence appears'),
    el('label', { for: `${p}-days-before` }, 'Days before the occurrence'),
    daysBefore,
    el('label', { for: `${p}-post-time` }, 'At this time'),
    postTime,
    el(
      'p',
      { class: 'muted small' },
      'Each occurrence is created and announced at this time, that many days ahead. ' +
        'LEAVE THE TIME BLANK and the series never creates anything on its own: ' +
        'you add occurrences with the “Create next occurrence” button.',
    ),
  ]);

  function isRepeating(): boolean {
    return typeSelect.value !== NO_REPEAT;
  }

  function render(): void {
    const repeating = isRepeating();
    const type = typeSelect.value as RecurrenceType;
    weekdayRow.hidden = !repeating || type !== 'weekly';
    intervalRow.hidden = !repeating || type !== 'custom';
    untilRow.hidden = !repeating;
    scheduleBlock.hidden = !repeating;
  }

  typeSelect.addEventListener('change', () => {
    render();
    opts.onChange?.();
  });
  render();

  return {
    typeRow,
    weekdayRow,
    intervalRow,
    untilRow,
    scheduleBlock,
    isRepeating,
    value(): RecurrenceChoice {
      if (!isRepeating()) return { repeats: false };
      const type = typeSelect.value as RecurrenceType;
      return {
        repeats: true,
        recurrenceType: type,
        // Sent only for the type that uses it. The server rejects an empty
        // weekly set rather than defaulting it to the anchor's weekday, so
        // there is nothing to fill in here.
        recurrenceDays: type === 'weekly' ? WEEKDAYS.filter((d) => dayBoxes.get(d)!.checked) : null,
        recurrenceInterval: type === 'custom' ? Number(intervalInput.value || 0) : null,
        endDate: until.value || null,
        feedPostDaysBefore: Number(daysBefore.value || 0),
        feedPostTime: postTime.value || null,
      };
    },
  };
}

/** A TIME column round-trips as HH:MM:SS; <input type="time"> wants HH:MM. */
function hhmm(time: string | null | undefined): string {
  return time ? time.slice(0, 5) : '';
}

function numInput(id: string, min: number, max: number, value: string): HTMLInputElement {
  const node = el('input', { id, type: 'number', min, max }) as HTMLInputElement;
  // Set the property too: `value` as an attribute is only the DEFAULT value, so
  // a re-render after a failed submit would otherwise show stale text.
  node.value = value;
  return node;
}
